import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowRight, BookOpen } from "lucide-react";
import { fetchLeadMagnet, BACKEND_URL } from "@/lib/api";

/**
 * Landing-page card advertising the current free ebook lead magnet.
 * Renders nothing when no ebook is flagged as a lead magnet in Mongo.
 */
export default function LeadMagnetCTA({ source = "landing" }) {
  const [ebook, setEbook] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const e = await fetchLeadMagnet();
        if (!cancelled) setEbook(e);
      } catch {
        if (!cancelled) setEbook(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!ebook) return null;

  return (
    <section
      className="mt-16 sm:mt-20 border border-terracotta/40 bg-terracotta/5 p-6 sm:p-10"
      data-testid="lead-magnet-cta"
      aria-labelledby="lead-magnet-heading"
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 sm:gap-10 items-center">
        <div className="border border-obsidian-600 bg-obsidian-800/40 aspect-[3/4] w-full flex items-center justify-center overflow-hidden">
          {ebook.cover_url ? (
            <img
              src={`${BACKEND_URL}${ebook.cover_url}`}
              alt={`Cover of ${ebook.title}`}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <BookOpen className="h-10 w-10 text-bone-500" aria-hidden="true" />
          )}
        </div>
        <div>
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2 inline-flex items-center gap-2">
            <Download className="h-3 w-3" aria-hidden="true" />
            Free download
          </p>
          <h2
            id="lead-magnet-heading"
            className="font-serif text-3xl sm:text-4xl text-bone-100 leading-tight mb-2"
          >
            {ebook.title}
          </h2>
          {ebook.subtitle && (
            <p className="font-serif italic text-bone-300 text-lg mb-4">{ebook.subtitle}</p>
          )}
          <p className="font-serif text-bone-400 leading-relaxed mb-6 line-clamp-3">
            {ebook.description ||
              "A short book you can keep — a slow read for the parts of you that were never spoken aloud."}
          </p>
          <Link
            to={`/free-ebook?s=${encodeURIComponent(source)}`}
            data-testid="lead-magnet-cta-btn"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-[0.24em] text-xs px-6 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
          >
            Send it to my inbox <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
